import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Users, ShieldOff, ShieldCheck, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useSystemSettings } from "../lib/systemSettings";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Kunden() {
  const { brandName } = useSystemSettings();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", club_card_id: "", email: "", phone: "" });

  async function reload() {
    try {
      const r = await api.get("/customers");
      setItems(r.data);
    } catch (err) {
      toast.error("Kunden konnten nicht geladen werden");
    }
  }
  useEffect(() => { reload(); }, []);

  async function create() {
    try {
      const clean = Object.fromEntries(Object.entries(form).filter(([_, v]) => v));
      await api.post("/customers", clean);
      toast.success("Kunde angelegt");
      setShowForm(false);
      setForm({ first_name: "", last_name: "", club_card_id: "", email: "", phone: "" });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Fehlgeschlagen");
    }
  }

  async function deactivate(c) {
    if (!window.confirm(`Kunde "${c.first_name} ${c.last_name}" deaktivieren?`)) return;
    try {
      await api.post(`/customers/${c.id}/deactivate`);
      toast.success("Deaktiviert");
      reload();
    } catch (err) {
      toast.error("Deaktivierung fehlgeschlagen");
    }
  }

  async function activate(c) {
    try {
      await api.post(`/customers/${c.id}/activate`);
      toast.success("Aktiviert");
      reload();
    } catch (err) {
      toast.error("Aktivierung fehlgeschlagen");
    }
  }

  async function remove(c) {
    if (!window.confirm(
      `Kunde "${c.first_name} ${c.last_name}" endgültig löschen?\n\n` +
      `Historische Käufe, Belege und Club-Karten-Historie bleiben erhalten. ` +
      `Der Kunde erscheint danach nicht mehr in der Liste.`
    )) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Gelöscht");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Löschen fehlgeschlagen");
    }
  }

  const Actions = ({ c, prefix }) => (
    <div className="flex flex-wrap gap-2">
      {c.active ? (
        <Button size="sm" variant="outline" className="border-zinc-700 text-rose-300"
          data-testid={`${prefix}deactivate-${c.id}`} onClick={() => deactivate(c)}>
          <ShieldOff size={14} className="mr-1" /> Deaktivieren
        </Button>
      ) : (
        <Button size="sm" variant="outline" className="border-zinc-700 text-emerald-300"
          data-testid={`${prefix}activate-${c.id}`} onClick={() => activate(c)}>
          <ShieldCheck size={14} className="mr-1" /> Aktivieren
        </Button>
      )}
      <Button size="sm" variant="outline" className="border-zinc-700 text-rose-400"
        data-testid={`${prefix}delete-${c.id}`} onClick={() => remove(c)}>
        <Trash2 size={14} className="mr-1" /> Löschen
      </Button>
    </div>
  );

  return (
    <div data-testid="kunden-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Users className="text-cyan-400 shrink-0" size={26} />
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight truncate">{brandName} Club Kunden</h1>
        </div>
        <Button data-testid="kunden-new-btn" onClick={() => setShowForm((s) => !s)} className="w-full sm:w-auto">
          <UserPlus size={16} className="mr-2" /> Neuer Kunde
        </Button>
      </div>

      {showForm && (
        <div className="mb-6 border border-zinc-800 bg-zinc-950 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Input placeholder="Vorname *" data-testid="kunden-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          <Input placeholder="Nachname *" data-testid="kunden-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          <Input placeholder="Club-Karte (QR/Barcode)" data-testid="kunden-club-card" value={form.club_card_id} onChange={(e) => setForm({ ...form, club_card_id: e.target.value })} />
          <Input placeholder="E-Mail" data-testid="kunden-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input placeholder="Telefon" data-testid="kunden-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Button data-testid="kunden-create-btn" disabled={!form.first_name || !form.last_name} onClick={create}>Anlegen</Button>
        </div>
      )}

      {/* Mobile cards */}
      <div className="md:hidden space-y-2" data-testid="kunden-mobile-list">
        {items.map((c) => (
          <div key={c.id} data-testid={`kunde-m-${c.id}`} className="border border-zinc-800 rounded-lg p-4 bg-zinc-950">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{c.first_name} {c.last_name}</div>
                <div className="text-xs font-mono text-zinc-500 truncate">{c.club_card_id || "—"}</div>
                {(c.email || c.phone) && (
                  <div className="text-xs text-zinc-400 mt-1 break-anywhere">
                    {[c.email, c.phone].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
              <div className="text-xs uppercase shrink-0">
                {c.active ? <span className="text-cyan-400">aktiv</span> : <span className="text-zinc-500">inaktiv</span>}
              </div>
            </div>
            <div className="mt-3">
              <Actions c={c} prefix="kunden-m-" />
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="border border-zinc-800 rounded-lg p-6 text-center text-zinc-500 text-sm">Keine Kunden</div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block border border-zinc-800 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-400 uppercase text-xs">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Club-Karte</th>
              <th className="p-3 text-left">Kontakt</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-zinc-800" data-testid={`kunde-${c.id}`}>
                <td className="p-3">{c.first_name} {c.last_name}</td>
                <td className="p-3 font-mono">{c.club_card_id || "—"}</td>
                <td className="p-3 text-zinc-400">{[c.email, c.phone].filter(Boolean).join(" · ") || "—"}</td>
                <td className="p-3">{c.active ? <span className="text-cyan-400">aktiv</span> : <span className="text-zinc-500">inaktiv</span>}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex">
                    <Actions c={c} prefix="kunden-" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
